import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads something for each of many projects (a workspace's), a few at a time (`concurrency`), so a workspace of twenty
 * projects never sends twenty requests at once. `load(projectId, mode)` resolves to the value; a project is loaded once
 * per `mode` (a different mode, e.g. a heavier read, loads it again unless `covers(loadedMode, wantedMode)` says the one
 * already read says enough), unless `reload()` clears them all.
 * Returns `{ entries, reload }`; `entries[projectId]` is `{ status: 'loading' | 'ready' | 'error', value, error, mode }`
 * (absent until its turn comes), so a cell can say "loading" and a failure of one project never hides the others.
 * @param {string[]} projectIds
 * @param {(projectId: string, mode: string) => Promise<any>} load
 * @param {{ enabled?: boolean, concurrency?: number, mode?: string, covers?: (loaded: string, wanted: string) => boolean }} [options]
 */
export default function useLazyByProject(projectIds, load, { enabled = true, concurrency = 3, mode = 'default', covers = (loaded, wanted) => loaded === wanted } = {}) {
  const [entries, setEntries] = useState({});
  const done = useRef(new Map()); // projectId -> mode loaded (or in flight)
  const queue = useRef([]);
  const inFlight = useRef(0);
  const live = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;
  const generation = useRef(0);

  const pump = useCallback(() => {
    while (live.current && inFlight.current < concurrency && queue.current.length) {
      const { projectId, mode: wanted, gen } = queue.current.shift();
      inFlight.current += 1;
      Promise.resolve()
        .then(() => loadRef.current(projectId, wanted))
        .then((value) => ({ status: 'ready', value, error: null, mode: wanted }))
        .catch((error) => ({ status: 'error', value: null, error, mode: wanted }))
        .then((entry) => {
          if (!live.current || gen !== generation.current) { return; }
          // A lighter read that finishes after the heavier one must not replace it.
          setEntries((prev) => (prev[projectId]?.status === 'ready' && covers(prev[projectId].mode, wanted) && !covers(wanted, prev[projectId].mode) ? prev : { ...prev, [projectId]: entry }));
        })
        .finally(() => {
          inFlight.current -= 1;
          pump();
        });
    }
  }, [concurrency]);

  const key = projectIds.join('|');
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  useEffect(() => {
    if (!enabled) { return; }
    const fresh = [];
    projectIds.forEach((projectId) => {
      const loaded = done.current.get(projectId);
      if (loaded !== undefined && covers(loaded, mode)) { return; }
      done.current.set(projectId, mode);
      fresh.push(projectId);
      queue.current.push({ projectId, mode, gen: generation.current });
    });
    if (fresh.length) {
      // Their cells say "loading" (what was read in another mode stays until the new answer comes).
      setEntries((prev) => {
        const next = { ...prev };
        fresh.forEach((projectId) => { next[projectId] = { ...(prev[projectId] || { value: null, error: null }), status: 'loading', mode }; });
        return next;
      });
      pump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, mode, pump]);

  /** Forgets every answer and asks again. */
  const reload = useCallback(() => {
    generation.current += 1;
    queue.current = [];
    done.current = new Map();
    setEntries({});
    const gen = generation.current;
    projectIds.forEach((projectId) => {
      done.current.set(projectId, mode);
      queue.current.push({ projectId, mode, gen });
    });
    setEntries(Object.fromEntries(projectIds.map((projectId) => [projectId, { status: 'loading', value: null, error: null, mode }])));
    pump();
  }, [projectIds, mode, pump]);

  return { entries, reload };
}
